// views/HowToPlayView.jsx: Phase 3 #5b: "How to Play" overlay.
//
// Rendered at the TOP of App.jsx, ABOVE/outside RoomView, as a fixed
// full-screen panel. Because it is a sibling layered on top, never a
// route that replaces RoomView, the live view tree (and with it the
// room WebSocket owner useRoomWs) stays mounted underneath the whole
// time it is open. Closing it returns the user to exactly the screen
// they were on, untouched.
//
// Content is a concise, accurate standard-Euchre summary (24-card deck,
// bowers, ordering up vs. naming trump, loner, scoring). The target
// score and the stick-the-dealer line follow the room's settings when
// open in a room (B2-10), defaulting to 10 and on.

import '../styles/how-to-play.css';
import {
  howToPlayTargetPoints,
  howToPlayRoundTwoTail,
  howToPlayStickParagraph,
} from './how-to-play-copy.js';
import { howToPlayScoringLines } from '../ui/scoring-table.js';

/**
 * @param {{
 *   onClose: () => void,
 *   targetScore?: 7|10,
 *   stickTheDealer?: boolean,
 * }} props
 */
export function HowToPlayView({ onClose, targetScore = 10, stickTheDealer = true }) {
  return (
    <div
      class="how-to-play cream"
      data-testid="how-to-play"
      role="dialog"
      aria-modal="true"
      aria-labelledby="how-to-play-title"
    >
      <div class="how-to-play__panel">
        <header class="how-to-play__header">
          <h2 id="how-to-play-title" class="how-to-play__title">How to Play Euchre</h2>
          <button
            type="button"
            class="how-to-play__close"
            data-testid="how-to-play-close"
            onClick={onClose}
            aria-label="Close How to Play"
          >
            ← Back
          </button>
        </header>

        <div class="how-to-play__body">
          <section class="how-to-play__section">
            <h3>The basics</h3>
            <p>
              Euchre uses a <strong>24-card deck</strong> — 9, 10, J, Q, K, A in each of
              ♠ ♥ ♦ ♣. Four players form two partnerships; partners sit across from each
              other. The first team to <strong>{howToPlayTargetPoints({ targetScore, stickTheDealer })}</strong> wins.
            </p>
            <p>
              Each player is dealt <strong>5 cards</strong>. The next card is turned face
              up — the <strong>up-card</strong>.
            </p>
          </section>

          <section class="how-to-play__section">
            <h3>Making trump</h3>
            <p>
              <strong>Round 1:</strong> in turn, each player may <em>order up</em> the
              up-card's suit as trump (the dealer then picks it up and discards one card)
              or pass.
            </p>
            <p>
              <strong>Round 2</strong> (if all four pass): the up-card is turned down.
              In turn, each player may <em>name</em> any other suit as trump
              {howToPlayRoundTwoTail({ targetScore, stickTheDealer })}
            </p>
            <p data-testid="how-to-play-stick">
              {howToPlayStickParagraph({ targetScore, stickTheDealer })}
            </p>
          </section>

          <section class="how-to-play__section">
            <h3>Bowers &amp; trump rank</h3>
            <p>
              The <strong>Jack of the trump suit</strong> is the <strong>right bower</strong> —
              the highest trump. The other Jack of the <em>same color</em> is the
              <strong> left bower</strong>: second-highest, and it counts as trump (not its
              printed suit).
            </p>
            <p class="how-to-play__rank">
              <strong>Trump, high → low:</strong> right bower, left bower, A, K, Q, 10, 9.
              Non-trump suits rank A → 9 normally.
            </p>
          </section>

          <section class="how-to-play__section">
            <h3>Going alone</h3>
            <p>
              The maker may go <strong>alone</strong> (a "loner"), playing the hand without
              their partner for a bigger reward.
            </p>
          </section>

          <section class="how-to-play__section">
            <h3>Playing the hand</h3>
            <p>
              Five tricks are played. You must <strong>follow the led suit</strong> if you
              can; otherwise play anything. The highest trump wins the trick — or, if no
              trump was played, the highest card of the led suit. The winner of each trick
              leads the next.
            </p>
          </section>

          <section class="how-to-play__section">
            <h3>Scoring</h3>
            {/* B4a-A1: the numbers come from the shared scoring table, the
                same one the go-alone confirmation reads, so they cannot drift. */}
            <ul class="how-to-play__scoring">
              {howToPlayScoringLines().map((line) => (
                <li key={line.lead}>{line.lead}<strong>{line.points}</strong></li>
              ))}
            </ul>
          </section>
        </div>

        <footer class="how-to-play__footer">
          <button
            type="button"
            class="how-to-play__done"
            data-testid="how-to-play-done"
            onClick={onClose}
          >
            Got it
          </button>
          <p class="how-to-play__credit" data-testid="font-credit">
            Fonts: Tiny5 and Silkscreen (SIL Open Font License), plus <a href="https://fonts.google.com/specimen/Nunito" target="_blank" rel="noreferrer">Nunito</a> by Vernon Adams (OFL)
          </p>
        </footer>
      </div>
    </div>
  );
}
